import React, { type ReactElement } from 'react';
import {
  Box,
  Button,
  ButtonIcon,
  Chip,
  FooterActionBar,
  Icon,
  Link as DSLink,
  Menu,
  SidePanel,
  Typography,
} from '@saviynt/design-system';

import {
  CF_NEU_STL,
  CF_SEC_BLD,
  ENTITLEMENT_DEATILS_TABS,
  type EntitlementRowType,
  violationsEntitlement,
} from '../../../../shared/constants';
import { useInvertedLocalize, useNavigateTo } from '../../../../shared/hooks';

import './SodEntitlementSidePanel.css';

interface SodEntitlementSidePanelProps {
  isSidePanelOpen: boolean;
  handleCloseSidePanel: () => void;
  selectedRow: EntitlementRowType | null;
}

/**
 * * TODO
 * - Write a description
 * - Sidepanel kabob menu needs to be functional
 * - Sidepanel Chip color needs to be dynamic based on the detail value
 */
function SodEntitlementSidePanel({
  isSidePanelOpen,
  handleCloseSidePanel,
  selectedRow,
}: SodEntitlementSidePanelProps): ReactElement {
  const l = useInvertedLocalize();

  const { navigateToDetails } = useNavigateTo();

  const handleSourceChange = (sourceType: string, activeTab: string) => {
    navigateToDetails({
      key: '1234567890',
      params: {
        title: 'OracleCloud_GL_SOD_Enter Journal & Post Journal',
        user: 'Joey Kookamunga',
        type: sourceType,
        activeTab,
      },
    });
  };

  const sodSidePanelLayout = () => (
    <>
      {/* 3 Col Link Grid */}
      <Box className='SodEntitlementSidePanel-detailLinks'>
        <Box className='SodEntitlementSidePanel-detailLinkBlock'>
          <Typography kind='body3' color={CF_NEU_STL}>
            Inherent Risks
          </Typography>
          <DSLink
            kind='button'
            onClick={() => {
              handleSourceChange(
                violationsEntitlement,
                ENTITLEMENT_DEATILS_TABS.TAB_RISKS
              );
            }}
            text={String(selectedRow?.inherentRisks)}
            size='large'
            prefixIconKind='CriticalHighRiskHexagon'
            suffixIconKind='ArrowRight'
          />
        </Box>
        <Box className='SodEntitlementSidePanel-detailLinkBlock'>
          <Typography kind='body3' color={CF_NEU_STL}>
            Users
          </Typography>
          <DSLink
            kind='button'
            onClick={() => {
              handleSourceChange(
                violationsEntitlement,
                ENTITLEMENT_DEATILS_TABS.TAB_USERS
              );
            }}
            text={String(selectedRow?.users)}
            size='large'
            prefixIconKind='User'
            suffixIconKind='ArrowRight'
          />
        </Box>
        <Box className='SodEntitlementSidePanel-detailLinkBlock'>
          <Typography kind='body3' color={CF_NEU_STL}>
            Violations
          </Typography>
          <DSLink
            kind='button'
            onClick={() => {
              handleSourceChange(
                violationsEntitlement,
                ENTITLEMENT_DEATILS_TABS.TAB_VIOLATIONS
              );
            }}
            text={String(selectedRow?.violations)}
            size='large'
            prefixIconKind='SOD'
            suffixIconKind='ArrowRight'
          />
        </Box>
      </Box>
      {/* Detail Chips */}
      <Box className='SodEntitlementSidePanel-detailChips'>
        <Box className='SodEntitlementSidePanel-detailChipBlock'>
          <Typography kind='body3' color={CF_NEU_STL}>
            {l('Entitlement Type')}
          </Typography>
          <Typography kind='body2' color={CF_SEC_BLD}>
            {selectedRow?.entitlementType}
          </Typography>
        </Box>
        <Box className='SodEntitlementSidePanel-detailChipBlock'>
          <Typography kind='body3' color={CF_NEU_STL}>
            {l('Application')}
          </Typography>
          <Typography kind='body2' color={CF_SEC_BLD}>
            This is where application would go!
          </Typography>
        </Box>
        <Box className='SodEntitlementSidePanel-detailChipBlock'>
          <Typography kind='body3' color={CF_NEU_STL}>
            {l('Description')}
          </Typography>
          <Typography kind='body2' color={CF_SEC_BLD}>
            {selectedRow?.description}
          </Typography>
        </Box>
        <Box className='SodEntitlementSidePanel-detailChipBlock'>
          <Typography kind='body3' color={CF_NEU_STL}>
            {l('Sox Critical')}
          </Typography>
          <Chip
            label={selectedRow?.soxCritical}
            kind='outlined'
            size='xSmall'
            color='critical'
            isInteractionDisabled
          />
        </Box>
        <Box className='SodEntitlementSidePanel-detailChipBlock'>
          <Typography kind='body3' color={CF_NEU_STL}>
            {l('Sys Critical')}
          </Typography>
          <Chip
            label={selectedRow?.sysCritical}
            kind='outlined'
            size='xSmall'
            color='critical'
            isInteractionDisabled
          />
        </Box>
        <Box className='SodEntitlementSidePanel-detailChipBlock'>
          <Typography kind='body3' color={CF_NEU_STL}>
            {l('Risk')}
          </Typography>
          <Chip
            label={selectedRow?.risk}
            kind='outlined'
            size='xSmall'
            color='critical'
            isInteractionDisabled
          />
        </Box>
        <Box className='SodEntitlementSidePanel-detailChipBlock'>
          <Typography kind='body3' color={CF_NEU_STL}>
            {l('Privileged')}
          </Typography>
          <Chip
            label={selectedRow?.privileged}
            kind='outlined'
            size='xSmall'
            color='critical'
            isInteractionDisabled
          />
        </Box>
        <Box className='SodEntitlementSidePanel-detailChipBlock'>
          <Typography kind='body3' color={CF_NEU_STL}>
            {l('Financial')}
          </Typography>
          <Chip
            label={selectedRow?.financial}
            kind='outlined'
            size='xSmall'
            color='critical'
            isInteractionDisabled
          />
        </Box>
        <Box className='SodEntitlementSidePanel-detailChipBlock'>
          <Typography kind='body3' color={CF_NEU_STL}>
            {l('Update Date')}
          </Typography>
          <Typography kind='body2' color={CF_SEC_BLD}>
            {selectedRow?.updateDate}
          </Typography>
        </Box>
      </Box>
    </>
  );

  const sodSidePanelFooter = () => (
    <FooterActionBar className='SodEntitlementSidePanel-footer'>
      <Button kind='subtle' isDisabled>
        {/* // TODO: Add localization to the consts */}
        {l('View Detail')}
      </Button>
      <Menu
        options={[]}
        onChange={() => {}}
        trigger={
          <ButtonIcon
            size='medium'
            kind='ghost'
            icon={<Icon kind='MoreVertical' />}
            isDisabled
          />
        }
        placement='top-end'
      />
    </FooterActionBar>
  );

  if (isSidePanelOpen) {
    return (
      <Box className='SodEntitlementSidePanel-sidePanelWrapper'>
        <SidePanel
          isOpen={isSidePanelOpen}
          onClose={handleCloseSidePanel}
          header={{
            title: selectedRow?.title,
            description: l('Entitlement'),
            icon: 'Entitlement',
          }}
          appIconSize='small'
          layout={sodSidePanelLayout()}
          footer={sodSidePanelFooter()}
          className='SodEntitlementSidePanel-sidePanel'
          layoutClassName='SodEntitlementSidePanel-sidePanelLayout'
          headerClassName='SodEntitlementSidePanel-sidePanelHeader'
          innerContentClassName='SodEntitlementSidePanel-sidePanelInnerContent'
          footerClassName='SodEntitlementSidePanel-sidePanelFooter'
        />
      </Box>
    );
  }

  return <Box className='SodEntitlementSidePanel-sidePanelCloseFiller' />;
}

export default SodEntitlementSidePanel;
